import React, { useEffect, useState, useRef } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";

const mapContainerStyle = {
  width: "100%",
  height: "600px",
};

const FaultZones = () => {
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places", "drawing"],
  });

  useEffect(() => {
    fetch("/api/fault-zones")
      .then((res) => res.json())
      .then((data) => setZones(data))
      .catch((err) => console.error("Failed to fetch fault zones", err));
  }, []);

  const getMapCenter = () => {
    if (zones.length === 0) return { lat: 39.8, lng: -88.4 };
    const avgLat = zones.reduce((sum, z) => sum + z.lat, 0) / zones.length;
    const avgLng = zones.reduce((sum, z) => sum + z.lon, 0) / zones.length;
    return { lat: avgLat, lng: avgLng };
  };

  const handleMapLoad = (map) => {
    mapRef.current = map;

    if (window.google?.maps?.places && searchInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current);
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place.geometry?.location) {
          map.panTo(place.geometry.location);
          map.setZoom(14);
        }
      });
    }

    const drawingManager = new window.google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: true,
      drawingControlOptions: {
        position: window.google.maps.ControlPosition.TOP_CENTER,
        drawingModes: ["circle", "rectangle", "polygon"],
      },
      circleOptions: {
        fillColor: "#ff0000",
        fillOpacity: 0.2,
        strokeWeight: 2,
        clickable: false,
        editable: true,
        zIndex: 1,
      },
    });

    drawingManager.setMap(map);
  };

  if (!isLoaded) {
    return <div style={{ padding: "2rem" }}>Loading Google Maps...</div>;
  }

  return (
    <div style={{ padding: "2rem" }}>
      <button
        onClick={() => navigate("/dashboard")}
        style={{
          background: "#1976d2",
          color: "#fff",
          border: "none",
          padding: "0.5rem 1rem",
          borderRadius: "5px",
          cursor: "pointer",
          marginBottom: "1rem",
        }}
      >
        ← Return to Dashboard
      </button>

      <h2>⚡ Fault Zones</h2>

      <input
        type="text"
        ref={searchInputRef}
        placeholder="Search address..."
        style={{
          width: "300px",
          padding: "8px",
          fontSize: "14px",
          marginBottom: "1rem",
          borderRadius: "4px",
          border: "1px solid #ccc",
        }}
      />

      <div style={{ background: "#fff", padding: "1rem", borderRadius: "8px", marginTop: "1rem" }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={getMapCenter()}
          zoom={11}
          onLoad={handleMapLoad}
        >
          {zones.map((zone, idx) => (
            <Marker
              key={idx}
              position={{ lat: zone.lat, lng: zone.lon }}
              title={`Fault: ${zone.predictedCause}`}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 7,
                fillColor: "orange",
                fillOpacity: 0.9,
                strokeWeight: 1,
              }}
              onClick={() => setSelectedZone(zone)}
            />
          ))}

          {selectedZone && (
            <InfoWindow
              position={{ lat: selectedZone.lat, lng: selectedZone.lon }}
              onCloseClick={() => setSelectedZone(null)}
            >
              <div style={{ fontSize: "14px", lineHeight: "1.5" }}>
                <strong>Cause:</strong> {selectedZone.predictedCause}<br />
                <strong>Blink Count:</strong> {selectedZone.blinkCount}<br />
                <strong>Devices:</strong> {selectedZone.sampleDevices?.join(", ") || "—"}<br />
                <strong>Time Window:</strong> {selectedZone.timeWindow || "—"}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>
    </div>
  );
};

export default FaultZones;
